/**
 * Copyright ©2022 Drivepoint
 */

import React, {createRef, useContext, useEffect, useRef, useState} from "react";
import {List} from "@mui/material";
import {useLocation, useNavigate} from "react-router-dom";
import {StandardMenuItem} from "@components/DPMenu/DPMenu";
import NavigationPageItemMenu, {NavigationPageItemMenuInterface} from "@components/NavigationPageItemMenu/NavigationPageItemMenu";
import YesNoDialog, {YesNoDialogInterface} from "@components/YesNoDialog/YesNoDialog";
import useRoutes from "@hooks/useRoutes";
import useStateChange from "@hooks/useStateChange";
import ServiceRegistry from "@services/ServiceRegistry";
import Telemetry from "@services/telemetry/Telemetry";
import DrivepointUser from "@services/user/DrivepointUser";
import EventUtilities from "@utilities/EventUtilities";
import PageEditDialog from "../../../widgets/component/PageEditDialog";
import {DPErrorBoundaryContext} from "../../DPErrorBoundary";
import NavigationSectionItem from "./NavigationSectionItem/NavigationSectionItem";
import "./Navigation.less";

type ContextMenuProps = {
  x: number;
  y: number;
  item: any;
};

export type NavigationProps = {
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
};

export default function Navigation(props: NavigationProps): any {

  const yesNoDialog = useRef<YesNoDialogInterface>();
  const pageEditDialog = useRef<any>();
  const menu = createRef<NavigationPageItemMenuInterface>();
  const location = useLocation();
  const navigate = useNavigate();
  const routes = useRoutes(true);
  const user = useStateChange<DrivepointUser>("user");
  const company = useStateChange<Record<string, any>>("excel_company");
  const dataConfig = useStateChange<any>("data_config");
  const globalErrorContext = useContext(DPErrorBoundaryContext);

  const [navigationStates, setNavigationStates] = useState<any>();
  const [currentPage, setCurrentPage] = useState<any>();
  const [sectionContextMenu, setSectionContextMenu] = useState<ContextMenuProps | null>(null);

  useEffect(() => {
    setCurrentPage(ServiceRegistry.routeService.getPageByPathname(location.pathname));
  }, [location, routes]);

  useEffect(() => {
    Telemetry.track("page_change", {page_id: currentPage?.id, path: currentPage?.path, name: currentPage?.name, urlKey: currentPage?.urlKey});
  }, [currentPage]);

  function getTokenValue(token: string): string | null {
    switch (token) {
      case ":id":
        return "";
      case ":company": {
        const companyId = ServiceRegistry.companyService.company?.id;
        if (companyId) {
          return companyId;
        }
        const pathSegment = location.pathname.split("/")?.[1];
        if (pathSegment && !pathSegment.startsWith(":")) {
          return pathSegment;
        }
        globalErrorContext?.onDPErrorBoundary(new Error("Could not get company"));
        return null;
      }
      default:
        globalErrorContext?.onDPErrorBoundary(new Error("Could not get token"));
        return null;
    }
  }

  function replaceTokens(path: string): string {
    const tokens = path.match(new RegExp(/(:\w+)/g));
    if (tokens) {
      for (const token of tokens) {
        const tokenValue = getTokenValue(token);
        if (tokenValue != null) {
          path = path.replace(token, tokenValue);
        }
      }
    }
    if (path.includes(":")) {
      logger.warn("Navigation path still contains unreplaced tokens", path);
    }
    return path;
  }

  function getExternalUrl(page: any): string {
    if (page.id === "company.finance.my_model") {
      return dataConfig?.data_processing?.DBT_DASHBOARD_VARS?.plans_schema_version == 2
        ? company?.[page.urlKey]
        : company?.[page.legacyUrlKey];
    }
    return page.urlKey ? company?.[page.urlKey] : page.url;
  }

  function openExternalPage(page: any): void {
    if (user || page.public) {
      const url = getExternalUrl(page);
      if (url) {
        window.open(url, "_blank");
      } else if (page.path) {
        navigate(replaceTokens(page.path));
      }
    }
  }

  function onPageClick(event: any, page: any): void {
    EventUtilities.stop(event);
    Telemetry.track("user_navigate", page);
    if (page.type === "dazzler") {
      if (event.metaKey || event.ctrlKey) {
        window.open(`${window.location.origin}/${page.company}/dazzler/report/${page.id}`);
        return;
      }
      navigate(`/${page.company}/dazzler/report/${page.id}`);
    }
    if (page.external) {
      openExternalPage(page);
    } else if (page.path) {
      if (event.metaKey || event.ctrlKey) {
        window.open(`${window.location.origin}${replaceTokens(page.path)}`);
        return;
      }
      let path = replaceTokens(page.path);
      if (page.menu?.token) {
        if (page.menu.token === "*") {
          path = path.replace("*", "");
        } else {
          const regex = new RegExp(`:${page.menu.token}|:${page.menu.token}?`);
          path = path.replace(regex, "");
        }
      }
      navigate(path);
    }
    if (props.onClose) { props.onClose(); }
  }

  function onMenuItemClick(page: any, item: StandardMenuItem | any, data?: any): void {
    let path = replaceTokens(page.path);
    if (item.token) {
      if (item.token === "*") {
        path = path.replace("*", item.id);
      } else {
        const regex = new RegExp(`:${item.token}|:${item.token}?`);
        path = path.replace(regex, item.id);
      }
    }
    navigate(path);
  }

  function getNavigationStates(): any {
    if (navigationStates) { return JSON.parse(navigationStates); }
    if (localStorage.navigationStates) { return JSON.parse(localStorage.navigationStates); }
    return {};
  }

  function setSectionState(section: any, open: boolean): void {
    const states = getNavigationStates();
    states[section.name] = open;
    localStorage.navigationStates = JSON.stringify(states);
    setNavigationStates(localStorage.navigationStates);
  }

  function isSectionOpen(section: any): boolean {
    if (!props.open) { return false; }
    const states = getNavigationStates();
    return states[section.name] ?? true;
  }

  function toggleSection(section: any): void {
    if (props.open) {
      setSectionState(section, !isSectionOpen(section));
    } else {
      if (props.onOpen) { props.onOpen(); }
    }
  }

  function onSectionContextMenu(event: any, section: any): void {
    EventUtilities.stop(event);
    if (user?.excelUser?.role !== "superAdmin") { return; }
    setSectionContextMenu(sectionContextMenu === null ? {item: section, x: event.clientX - 2, y: event.clientY - 4} : null);
  }

  function editPage(page: any): void {
    pageEditDialog.current?.edit(page);
  }

  function onPageEdit(page?: any): void {
    editPage(ServiceRegistry.routeService.getPageDefinitionById(page.id));
  }

  function renderSectionItem(section: any): any {
    if (section.hidden) { return; }
    return <NavigationSectionItem
      key={`section_${section.id}`}
      item={section}
      open={props.open}
      onSectionContextMenu={onSectionContextMenu}
      toggleSection={toggleSection}
      onPageClick={onPageClick}
      onMenuItemClick={onMenuItemClick}
    />;
  }

  function renderNavigationItems(): any {
    if (!routes.sections?.length || !routes.pages?.length) { return; }
    const topSections = routes.sections.filter(section => section.id !== "SECONDARY");
    const bottomSections = routes.sections.filter(section => section.id === "SECONDARY");
    return <List className="navigation-root-list">
      {topSections.map(section => renderSectionItem(section))}
      <div className="navigation-section-spacer" />
      {bottomSections.map(section => renderSectionItem(section))}
    </List>;
  }

  return <div className="navigation">
    <YesNoDialog ref={yesNoDialog} title="Confirm Delete" yes="DELETE" no="Cancel" />
    <PageEditDialog ref={pageEditDialog} />
    <NavigationPageItemMenu ref={menu} onPageEdit={onPageEdit} inNav />
    {renderNavigationItems()}
  </div>;

}
